<template>
  <component 
    :is="IconComponent" 
    v-if="IconComponent"
    :class="iconClasses"
    :size="iconSize"
    :stroke-width="strokeWidth"
    :color="color"
    v-bind="$attrs"
  />
  <div 
    v-else 
    :class="iconClasses"
    class="inline-flex items-center justify-center bg-gray-200 text-gray-500 text-xs font-mono"
  >
    ?
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { iconRegistry } from '@/icons'

// Props
const props = defineProps({
  name: {
    type: String,
    required: true
  },
  size: {
    type: [String, Number],
    default: 'md',
    validator: (value) => {
      // Allow size names or numbers
      return ['xs', 'sm', 'md', 'lg', 'xl', '2xl'].includes(value) || typeof value === 'number'
    }
  },
  strokeWidth: {
    type: [String, Number],
    default: 1.5
  },
  color: {
    type: String,
    default: 'currentColor'
  },
  class: {
    type: String,
    default: ''
  }
})

// Get the icon component from registry
const IconComponent = computed(() => {
  const iconName = props.name?.toLowerCase()
  return iconRegistry[iconName] || null
})

// Compute icon size
const iconSize = computed(() => {
  if (typeof props.size === 'number') {
    return props.size
  }
  
  const sizeMap = {
    'xs': 12,
    'sm': 16,
    'md': 20,
    'lg': 24,
    'xl': 28,
    '2xl': 32
  }
  
  return sizeMap[props.size] || 20
})

// Compute CSS classes
const iconClasses = computed(() => {
  const classes = []
  
  // Add size classes for Tailwind (fallback)
  const sizeClassMap = {
    'xs': 'w-3 h-3',
    'sm': 'w-4 h-4', 
    'md': 'w-5 h-5',
    'lg': 'w-6 h-6',
    'xl': 'w-7 h-7',
    '2xl': 'w-8 h-8'
  }
  
  if (typeof props.size === 'string' && sizeClassMap[props.size]) {
    classes.push(sizeClassMap[props.size])
  }
  
  // Add custom classes
  if (props.class) {
    classes.push(props.class)
  }
  
  return classes.join(' ')
})

// Inherit attributes for pass-through
defineOptions({
  inheritAttrs: false
})
</script>

<style scoped>
/* Ensure icons are inline and properly sized */
svg {
  display: inline-block;
  vertical-align: middle;
}
</style>
